import type { OAuthAccount } from '@frontend/api-types';
import {
  calendarAccessDeniedErrorMessage,
  calendarBlockedPopUpErrorMessage,
  calendarScopes,
  calendarSignInUnavailableErrorMessage,
} from '@frontend/constants';
import { useState } from '@frontend/react';
import { GmailIcon, GoogleCalendarIcon } from '@frontend/web-icons';
import { Button, Text, XStack, YStack, showToast } from '@frontend/web-react';
import { useDeactivateOAuthAccountMutation, useUpdateOAuthCodeMutation } from '@frontend/web/hooks';
import { Env, handleMutation, waitForGoogleIdentityServices } from '@frontend/web/utils';

type CalendarProps = {
  integratedCalendar?: OAuthAccount;
  purposes?: string[];
  refetchOAuthAccounts: () => void;
  scopes?: string[];
};

export const Calendar = ({
  integratedCalendar,
  purposes = ['calendar'],
  refetchOAuthAccounts,
  scopes = calendarScopes,
}: CalendarProps) => {
  const [deactivateOAuthAccount, { isLoading: isDeactivateOAuthAccountLoading }] = useDeactivateOAuthAccountMutation();
  const [updateOAuthCode] = useUpdateOAuthCodeMutation();
  const [isConnectCalendarLoading, setIsConnectCalendarLoading] = useState(false);

  const includesGmail = purposes.includes('gmail');
  const title = includesGmail ? 'Connect calendar and Gmail' : 'Connect calendar';
  const description = includesGmail
    ? "Avoid the dreaded scheduling back-and-forth by letting us see your availability and add events on your behalf. We'll also use your Gmail to identify opportunities to better support you."
    : "Avoid the dreaded scheduling back-and-forth by letting us see your availability and add events on your behalf. We'll use your calendar to identify opportunities to better support you";
  const successMessage = includesGmail ? 'Calendar and Gmail connected!' : 'Calendar connected!';

  const handleConnectionFailure = (response: { type?: string }) => {
    setIsConnectCalendarLoading(false);
    if (response.type === 'popup_closed') {
      showToast({ title: calendarAccessDeniedErrorMessage, variant: 'danger' });
    } else if (response.type === 'popup_failed_to_open') {
      showToast({ title: calendarBlockedPopUpErrorMessage, variant: 'danger' });
    }
  };

  const handleConnectCalendar = async () => {
    setIsConnectCalendarLoading(true);
    try {
      await waitForGoogleIdentityServices();
    } catch {
      showToast({ title: calendarSignInUnavailableErrorMessage, variant: 'danger' });
      setIsConnectCalendarLoading(false);
      return;
    }
    const client = google.accounts.oauth2.initCodeClient({
      callback: async (response: { code?: string; error?: string }) => {
        if (response.error) {
          showToast({ title: calendarAccessDeniedErrorMessage, variant: 'danger' });
          setIsConnectCalendarLoading(false);
        } else if (response.code) {
          await handleMutation({
            mutation: () =>
              updateOAuthCode({
                code: response.code!,
                provider: 'Google',
                purposes,
                scopes,
              }).unwrap(),
            onComplete: () => setIsConnectCalendarLoading(false),
            onSuccess: refetchOAuthAccounts,
            successMessage,
          });
        }
      },
      client_id: Env.VITE_GOOGLE_OAUTH_CLIENT_ID as string,
      error_callback: handleConnectionFailure,
      scope: scopes.join(' '),
      ux_mode: 'popup',
    });
    client.requestCode();
  };

  const handleDisconnectCalendar = async () => {
    if (integratedCalendar) {
      handleMutation({
        mutation: () => deactivateOAuthAccount({ id: integratedCalendar.id }).unwrap(),
        onSuccess: refetchOAuthAccounts,
        successMessage: 'Calendar disconnected!',
      });
    }
  };

  return (
    <YStack accessibilityLabel="Calendar" className="bg-trueWhite p-lg rounded-lg border-neutral-200 border-1">
      <YStack gap="lg">
        <XStack alignItems="center" justifyContent="between">
          <XStack alignItems="center" gap="md">
            <GoogleCalendarIcon />
            {includesGmail ? <GmailIcon /> : null}
            <Text type="p-md">{title}</Text>
          </XStack>
          <Button
            isLoading={integratedCalendar ? isDeactivateOAuthAccountLoading : isConnectCalendarLoading}
            label={integratedCalendar ? 'Disconnect' : 'Connect'}
            onClick={integratedCalendar ? handleDisconnectCalendar : handleConnectCalendar}
            size="sm"
            variant="primary"
          />
        </XStack>
        <Text className="max-w-screen-sm" color="tertiary" type="p-sm">
          {description}
        </Text>
      </YStack>
    </YStack>
  );
};
